import React from 'react';

/**
 * Event Card — compact post-tile used in grids (search, related events).
 * Business logo/name link to the business (no nested anchors);
 * event content links to the event. Poster shown when available.
 */
export default function EventCard({ event, business, location, showDate = true, analytics }) {
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatPrice = (price) => {
    if (!price) return '';
    if (price.amount && price.description) {
      return `$${price.amount} ${price.description}`;
    } else if (price.amount) {
      return `$${price.amount}`;
    } else if (price.description) {
      return price.description;
    }
    return '';
  };

  const handleClick = (label) => {
    if (analytics) {
      analytics.trackInteraction(label || 'click_event_card', {
        eventId: event.id,
        eventTitle: event.title,
        businessId: business?.id
      });
    }
  };

  const avatar = business?.avatar
    ? `https://tacxuksfiabbdygcfnqr.supabase.co/storage/v1/object/public/media/businesses/${business.id}/${business.avatar}`
    : null;
  const businessUrl = business ? `/businesses/${business.id}/` : `/events/${event.id}/`;
  const eventUrl = `/events/${event.id}/`;
  const dateText = new Date(event.eventDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <article className="h-full flex flex-col">
      {/* Instagram-style header: avatar, business name and handle live OUTSIDE the card */}
      <div className="flex items-center gap-3 min-w-0 mb-3">
        <a href={businessUrl} className="flex-shrink-0 no-underline" aria-label={business?.title || 'View event'}>
          {avatar ? (
            <img src={avatar} alt={business?.title} className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-gray-200" loading="lazy" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-brand-100 text-brand-800 font-bold flex items-center justify-center flex-shrink-0 text-lg">
              {(event.title || 'E').charAt(0).toUpperCase()}
            </div>
          )}
        </a>
        <div className="min-w-0 flex-1">
          <a href={businessUrl} className="no-underline">
            <p className="font-semibold text-gray-900 leading-tight truncate hover:text-brand-700 transition-colors">{business?.title || 'Pink Wallet'}</p>
          </a>
          {showDate && (
            <p className="text-sm text-gray-500 leading-tight truncate">
              {business?.handle ? (
                <a href={`https://instagram.com/${business.handle.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" className="no-underline hover:text-brand-700">
                  {business.handle}
                </a>
              ) : null}
              {business?.handle ? ' · ' : ''}
              {dateText}
            </p>
          )}
        </div>
      </div>

      {/* Bordered card — sharp (rounded-lg) corners, edge-to-edge poster at the top */}
      <div className="bg-white border border-gray-200/70 rounded-md overflow-hidden flex flex-col flex-1" style={{ boxShadow: '0 1px 2px rgb(0 0 0 / 0.04)' }}>
        {/* Poster when available — edge-to-edge full width, natural height, no crop */}
        {event.poster && (
          <a href={eventUrl} className="block no-underline" onClick={() => handleClick('click_event_poster')}>
            <img
              src={event.poster}
              alt={event.title}
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </a>
        )}

        <div className="flex flex-col flex-1 p-4 md:p-5">
          {/* Badges */}
          <div className="mb-2 flex gap-2 flex-wrap">
            {event.start_time && (
              <span className="badge badge-gray">{formatTime(event.start_time)}</span>
            )}
            {event.over_21 && <span className="badge badge-pink">21+</span>}
            {event.over_18 && <span className="badge badge-pink">18+</span>}
            {event.price && (
              <span className="badge badge-green font-semibold">{formatPrice(event.price)}</span>
            )}
            {business?.exclusive_offer?.discount && (
              <span className="badge badge-pink font-medium">{business.exclusive_offer.discount}</span>
            )}
          </div>

          {/* Event link content */}
          <a href={eventUrl} className="no-underline group flex-1 flex flex-col" onClick={() => handleClick()}>
            <h3 className="text-lg font-bold mb-1 text-gray-900 group-hover:text-brand-700 transition-colors">
              {event.title}
            </h3>

            {event.description && (
              <p className="text-gray-600 text-sm line-clamp-2">{event.description}</p>
            )}

            {location && (
              <p className="text-gray-500 text-sm mt-auto pt-2">
                {location.name}
              </p>
            )}
          </a>
        </div>
      </div>
    </article>
  );
}